import { assertNever } from "assert-never";

import { PluginState } from "./gen/ClientState";
import { CodeDiagnosticSeverity } from "./gen/CodeDiagnostics";
import { PathRelFile } from "./gen/Path";
import { LineCol, SrcSpan } from "./gen/SrcLoc";
import { HexGripApp } from "./HexGrip";
import { srcSpanContains } from "./SrcSpanUtils";

export interface CodeDiagnosticId {
  severity: CodeDiagnosticSeverity;
  srcSpan: SrcSpan;
  pluginName: string;
  codeDiagnosticIndex: number;
}

/**
 * @returns the results in an arbitrary order
 */
export function getCodeDiagnosticsAtPoint(
  pluginState: Map<string, PluginState>,
  filePath: PathRelFile,
  lineCol: LineCol
): { id: CodeDiagnosticId; message: string }[] {
  const result: { id: CodeDiagnosticId; message: string }[] = [];

  for (const [pluginName, state] of pluginState) {
    for (let i = 0; i < state.codeDiagnostics.length; ++i) {
      const codeDiagnostic = state.codeDiagnostics[i];
      if (codeDiagnostic.filePath === filePath && codeDiagnostic.srcSpan !== null) {
        if (srcSpanContains(codeDiagnostic.srcSpan, lineCol)) {
          result.push({
            id: {
              severity: codeDiagnostic.severity,
              pluginName: pluginName,
              srcSpan: codeDiagnostic.srcSpan,
              codeDiagnosticIndex: i
            },
            message: codeDiagnostic.message
          });
        }
      }
    }
  }

  return result;
}

export function appGetCodeDiagnosticsInEditorAtPoint(
  app: HexGripApp,
  filePath: PathRelFile,
  lineCol: LineCol
): { id: CodeDiagnosticId; message: string }[] {
  const editorTab = app.editorTabs.find((e) => e.filePath === filePath);
  if (editorTab === undefined) {
    return [];
  }

  if (editorTab.filePath === null) {
    return [];
  }

  const codeDiagnostics = getCodeDiagnosticsAtPoint(app.pluginState, editorTab.filePath, lineCol);
  sortCodeDiagnosticsBySize(codeDiagnostics);
  return codeDiagnostics;
}

function sortCodeDiagnosticsBySize(
  codeDiagnostics: { id: CodeDiagnosticId; message: string }[]
): void {
  codeDiagnostics.sort((a, b) => {
    return compareSrcSpans(a.id.srcSpan, b.id.srcSpan);
  });
}

function compareSrcSpans(a: SrcSpan, b: SrcSpan): number {
  switch (a.kind) {
    case "line":
      switch (b.kind) {
        case "line":
          return 0;
        case "lineCol":
          return 1;
        case "lineRange":
          return -1;
        case "lineColRange":
          return b.value.end.line !== b.value.start.line ? -1 : 1;
        default:
          return assertNever(b);
      }
    case "lineCol":
      switch (b.kind) {
        case "line":
          return -1;
        case "lineCol":
          return 0;
        case "lineRange":
          return -1;
        case "lineColRange":
          return -1;
        default:
          return assertNever(b);
      }
    case "lineRange":
      switch (b.kind) {
        case "line":
          return 1;
        case "lineCol":
          return 1;
        case "lineRange":
          return a.value.endLine - a.value.startLine - (b.value.endLine - b.value.startLine);
        case "lineColRange":
          return b.value.end.line - b.value.start.line > a.value.endLine - a.value.startLine
            ? -1
            : 1;
        default:
          return assertNever(b);
      }
    case "lineColRange":
      switch (b.kind) {
        case "line":
          return a.value.end.line !== a.value.start.line ? 1 : -1;
        case "lineCol":
          return 1;
        case "lineRange":
          return a.value.end.line - a.value.start.line > b.value.endLine - b.value.startLine
            ? 1
            : -1;
        case "lineColRange":
          if (a.value.end.line - a.value.start.line !== b.value.end.line - b.value.start.line) {
            return a.value.end.line - a.value.start.line - (b.value.end.line - b.value.start.line);
          } else {
            return b.value.start.col - a.value.start.col + (a.value.end.col - b.value.end.col);
          }
        default:
          return assertNever(b);
      }
    default:
      return assertNever(a);
  }
}
